import React, { useRef, useEffect, useState } from 'react';
import { makeStyles, IconButton } from '@material-ui/core';
import { KeyboardArrowDown } from '@material-ui/icons';
import { Message, Agent } from './types';
import { MessageBubble } from './MessageBubble';
import { TypingIndicator } from './TypingIndicator';

const useStyles = makeStyles(theme => ({
  messagesArea: {
    flex: 1,
    overflowY: 'auto',
    overflowX: 'hidden',
    padding: theme.spacing(2, 3),
    backgroundColor: '#fafafa',
    minHeight: 0,
    '&::-webkit-scrollbar': {
      width: '4px',
    },
    '&::-webkit-scrollbar-track': {
      background: 'transparent',
    },
    '&::-webkit-scrollbar-thumb': {
      background: theme.palette.grey[300],
      borderRadius: '2px',
      '&:hover': {
        background: theme.palette.grey[400],
      },
    },
  },
  messagesAreaSts: {
    backgroundColor: 'transparent !important',
    paddingBottom: '80px !important',
  },
  scrollButton: {
    position: 'absolute',
    bottom: theme.spacing(13),
    right: theme.spacing(4),
    backgroundColor: '#ff6b35',
    color: 'white',
    width: 38,
    height: 38,
    animation: '$bounce 2s infinite',
    '&:hover': {
      backgroundColor: '#e55a2b',
    },
  },
  '@keyframes bounce': {
    '0%, 20%, 50%, 80%, 100%': {
      transform: 'translateY(0)',
    },
    '40%': {
      transform: 'translateY(-10px)',
    },
    '60%': {
      transform: 'translateY(-5px)',
    },
  },
  container: {
    position: 'relative',
    flex: 1,
    minHeight: 0,
  },
}));

interface MessagesContainerProps {
  messages: Message[];
  agents: Agent[];
  isTyping: boolean;
  agentConfig: Agent;
  stsMode?: boolean;
  onSendMessage?: (text: string) => void;
}

export const MessagesContainer: React.FC<MessagesContainerProps> = ({
  messages,
  agents,
  isTyping,
  agentConfig,
  stsMode = false,
  onSendMessage,
}) => {
  const classes = useStyles();
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesAreaRef = useRef<HTMLDivElement>(null);
  const [showScrollButton, setShowScrollButton] = useState(false);

  const isAtBottom = () => {
    const element = messagesAreaRef.current;
    if (!element) return true;
    return element.scrollHeight - element.scrollTop <= element.clientHeight + 5;
  };

  const handleScroll = () => {
    if (isAtBottom()) {
      setShowScrollButton(false);
    }
  };

  useEffect(() => {
    if (messages.length === 0) return;

    const lastMessage = messages[messages.length - 1];
    if (lastMessage.type === 'user') {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
      setShowScrollButton(false);
    } else if (!isAtBottom()) {
      setShowScrollButton(true);
    }
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    setShowScrollButton(false);
  };

  return (
    <>
      <div
        ref={messagesAreaRef}
        className={`${classes.messagesArea} ${stsMode ? classes.messagesAreaSts : ''}`}
        onScroll={handleScroll}
      >
        {messages.map(message => (
          <MessageBubble
            key={message.id}
            message={message}
            agents={agents}
            stsMode={stsMode}
            onSendMessage={onSendMessage}
          />
        ))}
        <TypingIndicator
          isTyping={isTyping}
          agentConfig={agentConfig}
          stsMode={stsMode}
        />
        <div ref={messagesEndRef} />
      </div>
      {showScrollButton && (
        <IconButton className={classes.scrollButton} onClick={scrollToBottom}>
          <KeyboardArrowDown fontSize="large" />
        </IconButton>
      )}
    </>
  );
};
